import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  useCreate,
  useGetIdentity,
  useInvalidate,
  useUpdate,
} from '@refinedev/core'
import { User } from '@/utils/auth/auth0'
import EditIcon from '@mui/icons-material/Edit'
import { renderTextWithLinks } from '../utils/textUtils'

interface AdminComment {
  id: number
  comment: string
  from_user: string
  created_at: string
  from_user_auth0_id: string
}

interface AdminCommentsProps {
  adminComments: AdminComment[]
  funderUuid: string | null
  opportunityUuid: string | null
  onlyShowLatest?: boolean
}

export default function AdminComments({
  adminComments,
  funderUuid,
  opportunityUuid,
  onlyShowLatest = false,
}: AdminCommentsProps) {
  const [adding, setAdding] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [editing, setEditing] = useState<number | null>(null)
  const [editingComment, setEditingComment] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [savingNew, setSavingNew] = useState(false)

  const user = useGetIdentity().data as User

  const { mutate: createComment } = useCreate()
  const { mutate: updateComment } = useUpdate()
  const invalidate = useInvalidate()

  function handleEditingClick(id: number) {
    setEditing(id)
    setEditingComment(adminComments.find((c) => c.id === id)?.comment || '')
  }

  function handleStopEditing() {
    setEditing(null)
    setEditingComment('')
  }

  function handleUpdateComment() {
    if (editingComment === '' || editing === null) {
      return
    }
    if (!user || user.sub === null) {
      return
    }

    if (!funderUuid && !opportunityUuid) {
      return
    }

    setSavingEdit(true)

    updateComment(
      {
        resource: 'admin-comments',
        id: editing,
        values: {
          id: editing,
          comment: editingComment,
          from_user:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
          from_user_auth0_id: user?.sub,
        },
        meta: funderUuid
          ? { funder_uuid: funderUuid }
          : opportunityUuid
          ? { opportunity_uuid: opportunityUuid }
          : undefined,
      },
      {
        onSuccess: () => {
          invalidate({
            resource: funderUuid ? 'funders' : 'opportunities',
            invalidates: ['all'],
          })
          setEditing(null)
          setEditingComment('')
          setSavingEdit(false)
        },
        onError: () => {
          alert('Error updating comment. Please try again.')
          setSavingEdit(false)
        },
      }
    )
  }

  function handleCommentSubmit() {
    if (newComment === '') {
      return
    }
    if (!user || user.sub === null) {
      return
    }

    if (!funderUuid && !opportunityUuid) {
      return
    }

    setSavingNew(true)

    createComment(
      {
        resource: 'admin-comments',
        values: {
          comment: newComment,
          from_user:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
          from_user_auth0_id: user?.sub,
        },
        meta: funderUuid
          ? { funder_uuid: funderUuid }
          : opportunityUuid
          ? { opportunity_uuid: opportunityUuid }
          : undefined,
      },
      {
        onSuccess: () => {
          invalidate({
            resource: funderUuid ? 'funders' : 'opportunities',
            invalidates: ['all'],
          })
          setAdding(false)
          setNewComment('')
          setSavingNew(false)
        },
        onError: () => {
          alert('Error creating comment. Please try again.')
          setSavingNew(false)
        },
      }
    )
  }

  return (
    <Box
      sx={{
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        px: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        {onlyShowLatest ? (
          <Typography variant="h6">Latest Comment</Typography>
        ) : (
          <Typography variant="h4">Comments</Typography>
        )}
        {!adding && (
          <Button
            variant="contained"
            onClick={() => setAdding(!adding)}
            sx={{ alignSelf: 'flex-end' }}
          >
            Add Comment
          </Button>
        )}
      </Box>

      {adding && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
            }}
          >
            <TextField
              id="outlined-basic"
              variant="outlined"
              sx={{ width: '100%' }}
              multiline
              rows={2}
              placeholder="Add a comment"
              autoFocus
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
          </Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mx: 3 }}
          >
            <Button variant="outlined" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={savingNew || newComment === ''}
              onClick={() => handleCommentSubmit()}
            >
              {savingNew ? 'Saving...' : 'Submit'}
            </Button>
          </Box>
        </>
      )}

      <Box
        sx={{
          mt: onlyShowLatest ? 1 : 4,
          mb: onlyShowLatest ? 2 : 0,
          border: '1px solid #ccc',
          borderRadius: '5px',
          padding: 2,
        }}
      >
        {adminComments && adminComments.length > 0 ? (
          adminComments
            .slice(0, onlyShowLatest ? 1 : adminComments.length)
            .map((comment, index) => (
              <Box key={comment.id}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    width: '100%',
                    my: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {comment.from_user}
                    </Typography>
                    <Typography variant="caption">
                      {new Date(comment.created_at).toLocaleString('en-NZ', {
                        hour12: true,
                        timeZone: 'Pacific/Auckland',
                      })}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    {' '}
                    {editing === comment.id ? (
                      <Box sx={{ width: '100%' }}>
                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          sx={{ width: '100%' }}
                          multiline
                          rows={4}
                          placeholder="Add a comment"
                          autoFocus
                          value={editingComment}
                          onChange={(e) => setEditingComment(e.target.value)}
                        />
                        <Button
                          onClick={() => handleUpdateComment()}
                          disabled={savingEdit || editingComment === ''}
                        >
                          {savingEdit ? 'Saving...' : 'Save'}
                        </Button>
                        <Button onClick={() => handleStopEditing()}>
                          {' '}
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          width: '100%',
                          whiteSpace: 'pre-wrap',
                          my: 1,
                          ...(onlyShowLatest && {
                            maxHeight: '250px',
                            overflow: 'auto',
                            resize: 'vertical',
                          }),
                        }}
                      >
                        {renderTextWithLinks(comment.comment)}
                      </Typography>
                    )}
                    {comment.from_user_auth0_id === user?.sub &&
                      editing !== index && (
                        <IconButton
                          sx={{ alignSelf: 'flex-end' }}
                          onClick={() => handleEditingClick(comment.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                  </Box>
                </Box>
                {index < adminComments.length - 1 && !onlyShowLatest && (
                  <Divider />
                )}
              </Box>
            ))
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{ my: 1, width: '100%', textAlign: 'center' }}
            >
              {' '}
              No comments yet.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
